import { useEffect, useRef, useState } from "react"
import { useClickOutside } from "./useClickOutside"

export const useArrowKeyNav = ({ options, onCallback, onFocus, onBlur, activation }: any) => {
  const optionRefs = useRef([])
  const buttonRef = useRef(null)
  const containerRef = useRef(null)
  const searchInputRef = useRef(null)
  const [highlightedIndex, setHighlightedIndex] = useState(0)
  const [activated, setActivated] = useState(false)
  const [totalCount, setTotalCount] = useState(0)

  const handleBlur = () => {
    setActivated(false)
    onBlur?.()
  }

  useClickOutside(containerRef, handleBlur)

  useEffect(() => {
    if (activation === 'focus') {
      const handleFocus = () => {
        setActivated(true)
        onFocus()
      }
      buttonRef.current?.addEventListener('focus', handleFocus)
      return () => buttonRef.current?.removeEventListener('focus', handleFocus)
    }
  }, [activation, onFocus])

  useEffect(() => {
    if (highlightedIndex === 0 && searchInputRef.current) {
      searchInputRef.current.focus()
    } else if (optionRefs.current.length > 0) {
      optionRefs.current[highlightedIndex - (searchInputRef.current ? 1 : 0)]?.focus()
      setActivated(false)
    }
    setTotalCount(options?.length + (searchInputRef.current ? 1 : 0) || optionRefs.current.length)
  }, [highlightedIndex, activated])


  const handleContainerKeyDown = (e) => {
    if (activation === 'enter') {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault()
        setActivated(true)
        onFocus?.()
      }
    }
  }

  const handleOptionsKeyDown = (e) => {
    const hasSearch = !!searchInputRef.current

    switch (e.key) {
      case 'ArrowDown':
      case 'ArrowRight': {
        e.preventDefault()
        setHighlightedIndex((prev) => (prev + 1) % totalCount)
        break
      }
      case 'ArrowUp':
      case 'ArrowLeft': {
        e.preventDefault()
        setHighlightedIndex((prev) => (prev - 1 + totalCount) % totalCount)
        break
      }
      case 'Enter': {
        if (highlightedIndex === 0 && hasSearch) {
          searchInputRef.current?.focus()
          break
        }

        const adjustedIndex = highlightedIndex - (hasSearch ? 1 : 0)
        if (options?.[adjustedIndex]) {
          e.preventDefault()
          options[adjustedIndex].callback?.()
          onCallback?.(options[adjustedIndex])
        }
        containerRef.current?.focus()
        break
      }
      case 'Escape':
      case 'Tab': {
        handleBlur()
        break
      }
    }
  }

  return {
    handleContainerKeyDown,
    handleOptionsKeyDown,
    optionRefs,
    buttonRef,
    containerRef,
    searchInputRef,
    highlightedIndex
  }
}
